import React, { CSSProperties, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type {
	CustomUnitsDefinition,
	DeliveryBlockResourceLocationType,
	DynamicCapacityValuesOptions,
	Modify,
	ServiceArea
} from '@bringg/types';
import type { ImageUrl, MapboxClusterData, MapboxIconOptions } from '@bringg/react-components/dist/types/maps.consts';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import type { Column } from 'react-table';
import type { EtaResponseBreakItem } from '@bringg-frontend/eta-calculator';

import RoutesPlannerTimeline from '../routes-planner-timeline/routes-planner-timeline';
import Loader from '../../components/loader/loader';
import PlannerTable from '../planner-table/planner-table';
import usePlannerTable from './hooks/usePlannerTable';
import useStore from '../../stores/helpers/use-context';
import useMap from './hooks/useMap';
import PlannerMap from '../planner-map/planner-map';
import type { RoutesPlannerDeps, RoutesPlannerTask } from '../../types';
import { UpdatingSource } from '../types';
import MultipleTasksSelect from '../multiple-tasks-select/multiple-tasks-select';

export const ROUTE_PLANNER_CLASS_NAME = 'routes-planner-container';

export type RoutesPlannerBreak = Modify<
	EtaResponseBreakItem,
	{
		scheduled_start_time: string;
		scheduled_end_time: string;
		estimated_start_time: string;
		estimated_end_time: string;
	}
> & {
	run_id: number;
};

export type RoutesPlannerTimelineConfig = {
	axisBoundaryBufferHours: number;
	zoomMax: number;
	canSetTasksPastDeliveryBlock?: boolean;
};

export type KpiConfiguration = {
	isFairnessEnabled?: boolean;
};

export interface RoutesPlannerConfiguration {
	teamTimezone?: string;
	clusterConfig?: MapboxClusterData;
	timelineConfig?: RoutesPlannerTimelineConfig;
	handlingUnits?: CustomUnitsDefinition;
	enableVehicles?: boolean;
	isClusterMode?: boolean;
	countryCode?: string;
	hourFormat?: string;
	ignoreQuantityInLoadCalculation?: boolean;
	ignoreInventoryQuantityForCapacityCalculation?: boolean;
	uniqueInventoryFields?: string[];
	enableTaskPrice?: boolean;
	enableSkills?: boolean;
	dynamicCapacityMode?: DynamicCapacityValuesOptions;
	doSkillsIntersection?: boolean;
	initialLoadingTimeMinutes?: number;
	initialUnloadingTimeMinutes?: number;
	endOfWeekDay?: number;
	kpi?: KpiConfiguration;
}

export const DEFAULT_CLUSTER_CONFIGURATION = {
	shouldCluster: true,
	clusterBelowZoom: 11,
	clusterMinPoints: 5,
	clusterRadius: 50
};

export const DEFAULT_TIMELINE_CONFIGURATION = {
	axisBoundaryBufferHours: 12,
	zoomMax: 24 * 60 * 60 * 1000
};

export const DEFAULT_CONFIGURATION: RoutesPlannerConfiguration = {
	clusterConfig: DEFAULT_CLUSTER_CONFIGURATION,
	timelineConfig: DEFAULT_TIMELINE_CONFIGURATION
};

interface Props {
	mapApiKey: string;
	style?: CSSProperties;
	className?: string;
	mapZoom?: number;
	getAllServiceAreas: () => Promise<ServiceArea[]>;
	configuration: RoutesPlannerConfiguration;
	deps: RoutesPlannerDeps;
}

export type MapboxIconData = { icon: ImageUrl; iconOptions?: MapboxIconOptions };
export type MapboxJSXIconData = { icon: JSXIconWithToolTip; iconOptions?: MapboxIconOptions };
export type JSXIconWithToolTip = (color?: string, title?: string, id?: string) => JSX.Element;

export interface IconsProps {
	home?: MapboxJSXIconData;
	marker?: MapboxIconData;
	driver?: MapboxJSXIconData;
	parking?: MapboxJSXIconData;
}

export type StartEndMarker = {
	lat: number;
	lng: number;
	type: DeliveryBlockResourceLocationType;
	title: string;
};

const RoutesPlanner = (props: Props) => {
	const { className, style, getAllServiceAreas, mapApiKey, configuration, mapZoom } = props;
	const [selectedTasksIds, setSelectedTasksIds] = useState<number[]>([]);
	const { isInitModal, translations, ganttStore, multiSelectedEnabled, isOptimizationKpiEnabled } = useStore();

	const { onMapLoaded, highlightPopup, removeTaskIdPopup, resizeMap } = useMap();
	const [isMapExpanded, setIsMapExpanded] = useState<boolean>(true);
	const [multipleSelectModalVisible, setMultipleSelectModalVisible] = useState(false);
	const changeTasksSelection = (tasksIds: number[], source: UpdatingSource) => {
		if (!tasksIds.length) {
			closeMultipleSelectModal();
		}

		if (source !== UpdatingSource.Timeline) {
			const timeline = ganttStore.ganttApi.api;
			timeline.setSelection(tasksIds);
		}
		setSelectedTasksIds(tasksIds);
		setMultipleSelectModalVisible(tasksIds.length > 1);
	};

	const { columns, tableTasks, skipTablePageReset, totalRuns, runsToDisplay, setRunsToDisplay, toggleRuns } =
		usePlannerTable(props.deps, selectedTasksIds, changeTasksSelection, multiSelectedEnabled);

	const closeMultipleSelectModal = () => {
		setMultipleSelectModalVisible(false);
	};

	return (
		<div className={classNames(ROUTE_PLANNER_CLASS_NAME, className)} style={style}>
			<div className="planner-upper">
				<PlannerTable
					tasks={tableTasks}
					columns={columns as Column<RoutesPlannerTask>[]}
					onToggleRuns={toggleRuns}
					selectedRuns={new Set(runsToDisplay)}
					totalRuns={totalRuns}
					skipPageReset={skipTablePageReset}
					translations={translations.routesTableTranslations}
					onMouseEnter={highlightPopup}
					onMouseLeave={removeTaskIdPopup}
					changeTasksSelection={changeTasksSelection}
				/>
				<div className="planner-expand-shrink">
					<div className="separator-bg" />
					<div
						className={classNames('arrow-icon-bg', { 'is-expanded': !isMapExpanded })}
						title={
							translations.routesTableTranslations.actions[!isMapExpanded ? 'shrinkTable' : 'expandTable']
						}
						onClick={() => {
							setIsMapExpanded(!isMapExpanded);
						}}
					>
						<BringgIcon iconName={BringgFontIcons.Arrow} className="arrow-icon" />
					</div>
				</div>
				<PlannerMap
					mapApiKey={mapApiKey}
					mapZoom={mapZoom}
					getAllServiceAreas={getAllServiceAreas}
					clusterConfig={configuration.clusterConfig}
					onMapLoaded={onMapLoaded}
					isExpanded={isMapExpanded}
					resizeMap={resizeMap}
					changeTasksSelection={changeTasksSelection}
					isOptimizationKpiEnabled={isOptimizationKpiEnabled}
					countryCode={configuration.countryCode}
				/>
			</div>
			<div className="planner-footer">
				<RoutesPlannerTimeline
					onRemoveRow={async runId => setRunsToDisplay([runId], { show: false })}
					configuration={configuration.timelineConfig ?? DEFAULT_TIMELINE_CONFIGURATION}
					changeTasksSelection={changeTasksSelection}
				/>
			</div>
			{isInitModal && <Loader />}
			{multipleSelectModalVisible && (
				<MultipleTasksSelect selectedTasksIds={selectedTasksIds} changeTasksSelection={changeTasksSelection} />
			)}
		</div>
	);
};

export default observer(RoutesPlanner);
